import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'

import {
  CBadge,
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react-pro'
import { mdiArrowTopRight, mdiArrowUp, mdiClose, mdiWindowMinimize } from '@mdi/js'
import Icon from '@mdi/react'

import { ReactComponent as MagicEditIcon } from '@assets/images/magic-edit-icon.svg'
import GeneratorInput from '@components/atoms/GeneratorInput'
import SkeletonLoader from '@components/atoms/SkeletonLoader'
import { carolinaBlue, skyBlue } from '@constants/colors'
import { useGetStartedCheckList } from '@contexts/getStartedCheckListProvider'
import { useToast } from '@contexts/toastProvider'
import {
  ChatLogMessage,
  ChatLogMessageContent,
  TemplateType,
  useGuestUpdateTemplateChatLogMutation,
  useTemplateChatLogQuery,
  useUpdateTemplateChatLogMutation,
} from '@graphQLGenerated'
import { LoaderType } from '@interfaces/components/atoms/SkeletonLoader'
import { IChatMessageButton } from '@interfaces/components/organisms/ChatMessageModal'
import { WalkthroughType } from '@interfaces/components/organisms/Walkthrough'
import { getChatMessageIncentiveShown, setChatMessageIncentiveShown } from '@utils/localStorage'
import { analytics, SegmentEvent } from '@utils/segment'

import Walkthrough from '../Walkthrough'

import './styles.scss'

const ChatMessageButton: FC<IChatMessageButton> = ({
  templateId,
  selectedTemplate,
  currentContent,
  onClickForm,
}) => {
  const toast = useToast()
  const { setCheckListActionDone } = useGetStartedCheckList()

  const { data: initialChatLog } = useTemplateChatLogQuery({
    variables: {
      templateId: templateId || '',
    },
    skip: !templateId,
    fetchPolicy: 'network-only',
  })

  const [updateFormData, { loading: updateFormDataLoading }] = useUpdateTemplateChatLogMutation()
  const [guestUpdateFormData, { loading: guestUpdateFormDataLoading }] =
    useGuestUpdateTemplateChatLogMutation()

  const [modalVisible, setModalVisible] = useState(false)
  const [chatLogId, setChatLogId] = useState<string | null>()
  const [incentiveShown, setIncentiveShown] = useState(false)

  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<Array<ChatLogMessage | null>>([])

  const version = useRef(-1)
  const bottomDivRef = useRef<HTMLDivElement>(null)

  const initialMessages = useMemo(
    () => [
      {
        content: JSON.stringify({
          message: `Hi! Tell me what you want to see edited on this resource, and I'll update it for you.`,
        }),
        role: 'assistant',
      },
      {
        content: JSON.stringify({
          message: `You can always revert back to the original version by clicking on the button below called "Initial Version".`,
          content: selectedTemplate?.content,
        }),
        role: 'assistant',
      },
    ],
    [selectedTemplate?.content],
  )

  useEffect(() => {
    setIncentiveShown(getChatMessageIncentiveShown())
  }, [])

  useEffect(() => {
    if (initialChatLog?.templateChatLog?.messages.length) {
      setMessages([...initialMessages, ...initialChatLog?.templateChatLog?.messages])
    } else {
      setMessages(initialMessages)
    }
  }, [initialChatLog?.templateChatLog, initialMessages])

  useEffect(() => {
    if (modalVisible) {
      setTimeout(() => bottomDivRef.current?.scrollIntoView({ behavior: 'auto' }), 20)
    }
  }, [modalVisible])

  useEffect(() => {
    if (messages.length) {
      setTimeout(() => bottomDivRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
    }
  }, [messages])

  useEffect(() => {
    if (
      (selectedTemplate?.type === TemplateType.Intake ||
        selectedTemplate?.type === TemplateType.TreatmentPlan) &&
      selectedTemplate?.default
    ) {
      setTimeout(() => setModalVisible(true), 800)
    }
  }, [selectedTemplate?.default, selectedTemplate?.type])

  const handleClickForm = useCallback(
    (content: string) => {
      onClickForm(content)
      if (isMobile) {
        setModalVisible(false)
      }
    },
    [onClickForm],
  )

  const handleSubmitMessage = useCallback(() => {
    if (currentContent && input) {
      setMessages((prev) => [...prev, { role: 'user', content: input }])
      setInput('')
      // id = custom for generated, not yet saved templates.
      if (!templateId || templateId === 'custom') {
        guestUpdateFormData({
          variables: {
            content: currentContent,
            input,
            chatLogId,
          },
          onCompleted: (data) => {
            setMessages([...initialMessages, ...(data.guestUpdateTemplateChatLog?.messages || [])])
            if (!chatLogId) {
              setChatLogId(data.guestUpdateTemplateChatLog?.id)
            }
            const latestMessage =
              data.guestUpdateTemplateChatLog?.messages[
                data.guestUpdateTemplateChatLog?.messages.length - 1
              ]
            const latestMessageContent: ChatLogMessageContent = JSON.parse(
              latestMessage?.content || '{}',
            )
            if (latestMessageContent.content && !isMobile) {
              handleClickForm(
                typeof latestMessageContent.content === 'object'
                  ? JSON.stringify(latestMessageContent.content)
                  : latestMessageContent?.content,
              )
            }
          },
          onError: (error) => {
            if (error.message.includes('Too many requests')) {
              toast.show({
                message: 'AI credit limit exceeded, please sign-up or try again in 24 hours',
                state: 'error',
              })
            } else {
              toast.show({
                message: 'An error happened while trying to edit activity, please try again later',
                state: 'error',
              })
            }
          },
        })
      } else {
        updateFormData({
          variables: {
            content: currentContent,
            input,
            templateId,
          },
          onCompleted: (data) => {
            setCheckListActionDone('editAssignmentWithAI')

            setMessages([...initialMessages, ...(data.updateTemplateChatLog?.messages || [])])

            const latestMessage =
              data.updateTemplateChatLog?.messages[data.updateTemplateChatLog?.messages.length - 1]
            const latestMessageContent: ChatLogMessageContent = JSON.parse(
              latestMessage?.content || '{}',
            )
            if (latestMessageContent.content && !isMobile) {
              handleClickForm(
                typeof latestMessageContent.content === 'object'
                  ? JSON.stringify(latestMessageContent.content)
                  : latestMessageContent?.content,
              )
            }
          },
          onError: () => {
            toast.show({
              message: 'An error happened while trying to edit activity, please try again later',
              state: 'error',
            })
          },
        })
      }
    }
  }, [
    currentContent,
    input,
    templateId,
    guestUpdateFormData,
    chatLogId,
    initialMessages,
    handleClickForm,
    toast,
    updateFormData,
    setCheckListActionDone,
  ])

  const handleCloseFormModal = useCallback(() => {
    setModalVisible(false)
  }, [setModalVisible])

  const _renderModalContent = useMemo(
    () => (
      <>
        <CModalHeader closeButton={false} className="p-3">
          <CModalTitle className="d-flex align-items-center">
            <div className="me-2">🤖 Editor Assistant</div>
            <CBadge style={{ backgroundColor: skyBlue }}>Beta</CBadge>
          </CModalTitle>
          <div className="clickable" onClick={handleCloseFormModal}>
            <Icon path={isMobile ? mdiClose : mdiWindowMinimize} size={1} />
          </div>
        </CModalHeader>
        {!incentiveShown ? (
          <CModalBody className="p-0">
            <div className="CMBIncentiveModalBodyContainer">
              <h4 className="mb-2">👋 Hi!</h4>
              <div className="body-large-bold-font mb-2">Welcome to Edit Assist!</div>
              <div className="body-large-font">
                Quickly make edits to this assignment with our smart AI assistant.
              </div>
              <div className="body-large-font mb-4">
                Tell it what you want to modify, and it&apos;ll update it for you!
              </div>
              <CButton
                style={{ width: 'fit-content' }}
                onClick={() => {
                  setIncentiveShown(true)
                  setChatMessageIncentiveShown()
                }}
              >
                Ok, got it!
              </CButton>
            </div>
          </CModalBody>
        ) : (
          <>
            <CModalBody className="p-0">
              <div className="CMBModalBodyContainer">
                {messages.map((message, index) => {
                  if (index === 0) {
                    version.current = -1
                  }
                  if (message?.role === 'assistant') {
                    let data: ChatLogMessageContent | undefined = undefined
                    try {
                      data = JSON.parse(message.content || '{}')
                    } catch (e) {}
                    if (data?.content) {
                      version.current += 1
                    }
                    return (
                      <div className="mb-2 w-75 white-space-pre-wrap" key={message.content + index}>
                        <div className="CMBAssistantMessage">
                          {data?.message ||
                            'The connection request timed out, please try submitting your request once again.'}
                        </div>
                        {!!data?.content && (
                          <div
                            className="CMBMessageContentButton mt-2"
                            onClick={() => {
                              if (data?.content) {
                                handleClickForm(
                                  typeof data?.content === 'object'
                                    ? JSON.stringify(data.content)
                                    : data.content,
                                )
                              }
                            }}
                          >
                            <div className="p-2">
                              <div className="CMBMessageVersionTag mb-1">
                                <div className="caption-font">
                                  {version.current === 0
                                    ? 'Initial Version'
                                    : `Version ${version.current}`}
                                </div>
                              </div>
                              <div className="body-small-font">{selectedTemplate?.title}</div>
                            </div>
                            <div className="CMBMessageContentButtonSeparator" />
                            <Icon
                              className="align-self-end me-2 my-1"
                              path={mdiArrowTopRight}
                              color={carolinaBlue}
                              size={0.8}
                            />
                          </div>
                        )}
                      </div>
                    )
                  }
                  return (
                    <div className="CMBUserMessage my-4" key={`${message?.content} ${index}`}>
                      <div>{message?.content}</div>
                    </div>
                  )
                })}
                {(updateFormDataLoading || guestUpdateFormDataLoading) && (
                  <div className="w-75 white-space-pre-wrap">
                    <SkeletonLoader
                      className="CMBAssistantMessage w-100"
                      type={LoaderType.Prompt}
                    />
                  </div>
                )}
                <div ref={bottomDivRef} />
              </div>
            </CModalBody>
            <CModalFooter className="p-2 w-100 border-0">
              <GeneratorInput
                customIcon={mdiArrowUp}
                disabled={updateFormDataLoading || guestUpdateFormDataLoading || !input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onSubmit={handleSubmitMessage}
                placeholder="Type what you want to modify"
              />
            </CModalFooter>
          </>
        )}
      </>
    ),
    [
      handleCloseFormModal,
      incentiveShown,
      messages,
      updateFormDataLoading,
      guestUpdateFormDataLoading,
      input,
      handleSubmitMessage,
      selectedTemplate?.title,
      handleClickForm,
    ],
  )

  return (
    <div className="CMBContainer">
      <Walkthrough type={WalkthroughType.AIEditor} />
      {!isMobile ? (
        <>
          {modalVisible && (
            <div
              className="CMBModalContainer"
              style={{
                display: modalVisible ? undefined : 'none',
              }}
            >
              {_renderModalContent}
            </div>
          )}
        </>
      ) : (
        <CModal visible={modalVisible} scrollable alignment="center">
          {_renderModalContent}
        </CModal>
      )}
      <CButton
        className="CMBWalkthroughStep2"
        onClick={() => {
          setModalVisible((prev) => !prev)
          analytics.track(SegmentEvent.EDITOR_ASSISTANT_BUTTON_CLICKED)
        }}
      >
        <MagicEditIcon /> AI Editor
      </CButton>
    </div>
  )
}

export default ChatMessageButton
